import { useEffect } from "react";

import { api } from "@/services/api";
import { authSnapshot } from "@/store/auth";

/** Which tabs are open and when each was last heard from. */
const TABS_KEY = "mrm.tabs";

/** How many check-ins a tab may miss before the others count it as gone. */
const MISSES_ALLOWED = 2;

/**
 * The pace to keep when the server has not said what it wants.
 *
 * The policy carries the real number — a third of the window the session was
 * actually granted — but it arrives in a query, and a query can fail. Without
 * a fallback a single failed read of the policy meant this hook was handed
 * `undefined` and did nothing at all for the rest of the page's life: the
 * screen stayed open, nothing checked in, and the session lapsed under
 * somebody who was sitting right in front of it. Checking in too often costs
 * one empty request every few minutes; not checking in costs the session.
 */
export const FALLBACK_HEARTBEAT_SECONDS = 300;

type Registry = Record<string, number>;

/** Storage is allowed to fail — a private window, a browser blocking site data. */
const readRegistry = (): Registry => {
  try {
    const raw = window.localStorage.getItem(TABS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as Registry) : {};
  } catch {
    return {};
  }
};

const writeRegistry = (registry: Registry): void => {
  try {
    window.localStorage.setItem(TABS_KEY, JSON.stringify(registry));
  } catch {
    // Nothing to do. A tab that cannot record itself simply does not get to
    // vote on whether it was the last one, and the slot lapses on its own.
  }
};

/**
 * Hold this tab's claim on the account's one session slot, and give it back.
 *
 * The server releases an account after a stretch of not hearing from it, and
 * that stretch has to be short — it is how long somebody stays locked out of
 * their own login after closing a browser without signing out. Short is only
 * safe if a screen that is open but untouched still counts as present, which
 * is what this is: every authenticated request keeps the slot alive, so a tab
 * with nobody clicking in it sends one on a timer.
 *
 * It is not an idle timeout and does not fight with one. `useIdleTimeout`
 * decides when somebody has been away too long and signs them out properly.
 * This only distinguishes *open* from *gone*, and a tab sat at a login screen
 * nobody is watching is still open.
 *
 * **Closing is handled directly, not waited out.** The timer alone would leave
 * the account held for the rest of the window after the browser closed, which
 * is the whole complaint. So a tab leaving checks whether any other tab is
 * still checking in, and if none is, releases the session on the way out. The
 * registry is what makes that safe: closing one of three tabs must not sign
 * the other two out, and a tab that was killed rather than closed goes stale
 * on its own and stops holding the others hostage.
 *
 * **Leaving is not signing out, because a reload leaves too.** `pagehide` is
 * the browser saying this page is finished; it is not the browser saying
 * anybody has gone. A refresh of the screen, a full navigation and a closed
 * window all arrive here identically, and nothing available at that moment
 * tells them apart. Signing out on the way out therefore signed the person out
 * every time they reloaded: the page came back with its stored pair, the
 * session behind it had been revoked, and the first request said so.
 *
 * So the departing tab asks the server to *shorten* the account's hold rather
 * than end it — `/auth/release`, which revokes nothing. A page that comes back
 * reclaims the slot with the heartbeat it sends on mount; a browser that is
 * really gone stops holding the account a minute later. Signing out is still
 * `/auth/logout`, and it is still what the sign-out button sends.
 */
export function useSessionHeartbeat(seconds: number | undefined): void {
  useEffect(() => {
    if (!seconds || seconds <= 0) return;

    const period = seconds * 1000;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const announce = () => writeRegistry({ ...readRegistry(), [id]: Date.now() });

    /** Remove this tab, and say whether anyone else is still reporting in. */
    const departAndLookAround = (): boolean => {
      const registry = readRegistry();
      delete registry[id];
      const cutoff = Date.now() - period * (MISSES_ALLOWED + 1);
      const others = Object.entries(registry).filter(([, at]) => at > cutoff);
      writeRegistry(Object.fromEntries(others));
      return others.length > 0;
    };

    announce();
    // Straight away as well as on the timer: a tab opened moments before the
    // window lapses should not have to wait a full period to say so.
    void api.post("/auth/heartbeat").catch(() => undefined);

    const timer = setInterval(() => {
      announce();
      // Failure is not worth reporting. A heartbeat that does not land is a
      // heartbeat missed, which is already what the window allows for, and the
      // next real request the person makes does the same job.
      void api.post("/auth/heartbeat").catch(() => undefined);
    }, period);

    const leaving = (event: PageTransitionEvent) => {
      // Going into the back/forward cache, not going away: the page is frozen
      // whole and comes back as it was, with its timer and this listener still
      // attached. Nothing has been given up, so there is nothing to hand back.
      if (event.persisted) return;
      if (departAndLookAround()) return;
      const { refreshToken } = authSnapshot();
      if (!refreshToken) return;
      // `keepalive` outlives the page, which a normal request does not — the
      // browser is tearing it down and nothing here will be running to see the
      // request finish. Keyed on this session, so it cannot cut short one that
      // somebody else has since begun.
      try {
        void fetch(`${api.defaults.baseURL ?? ""}/auth/release`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken }),
          keepalive: true,
        }).catch(() => undefined);
      } catch {
        // A browser without `fetch` keepalive loses nothing it had before: the
        // slot lapses on the window instead of being handed back sooner.
      }
    };

    // `pagehide` rather than `unload`, which a page restored from the back
    // forward cache never fires and some browsers no longer fire at all.
    window.addEventListener("pagehide", leaving);

    return () => {
      clearInterval(timer);
      window.removeEventListener("pagehide", leaving);
      const registry = readRegistry();
      delete registry[id];
      writeRegistry(registry);
    };
  }, [seconds]);
}
